/* eslint-disable no-param-reassign, no-shadow */

import {
  assign,
  cloneDeep,
  omit,
  pick,
  uniq,
  uniqBy,
} from 'lodash-es';

import { sleep } from '../../../utils/helpers';

import Graphics from '../../../services/graphics';

import toast from '../../../components/toast/toast';
import { collectTimelineValues } from '../../../utils/timeline-collectors';

/**
 * Loop through timeline and find any graphics or graphic groups.
 * Then pull out the timeline_id, and filter out duplicates.
 * If we have a template in the lookup table, but it doesn't have an `elements` property,
 * we need to fetch it again to get all the data we need for the playlist (i.e. shortening URLs).
 * @param {Array<Object>} timeline - Producer playlist
 * @returns {Array<Number>} List of template IDs
 */
const getTemplateIds = (timeline, lookupTable = {}, notFoundMap = {}) => uniq([
  ...collectTimelineValues(timeline, (post) => {
    if (post.layout !== 'graphic' || !post.template_id) {
      return null;
    }

    if (post.template_id in lookupTable && lookupTable[post.template_id]?.elements) {
      return null;
    }

    if (notFoundMap[post.template_id]) {
      return null;
    }

    return post.template_id;
  }, new Set()),
]);

const state = {
  graphics: [],
  templates: [],
  templateTags: [],
  templatesLookupTable: {},
  lookupTable: {},
  assetModalOpen: false,
  assetModalOpenType: '',

  // Track which templates are being loaded by ID
  templatesLoadingMap: {},

  // Track which templates are confirmed missing (deleted) by ID
  templatesNotFoundMap: {},


  // Pagination options
  isLoading: false,
  page: 1,
  limit: 20,
  tags: [],
  search: '',
  total: 0,

  // Saved Graphics Pagination
  savedGraphics: {
    total: 0,
  },
};

const actions = {
  toggleAssetModal(context, opts) {
    context.commit('TOGGLE_ASSET_MODAL', opts);
  },

  /**
   * Get list of saved graphics
   */
  async getGraphics(context) {
    const { _id: productionId } = context.rootState.production.production;

    // We can remove `productionId` to remove the tie to productions
    // and just fetch ALL saved graphics
    const { graphics, total } = await Graphics.getGraphics({ productionId });

    context.commit('SET_GRAPHICS', graphics);
    context.commit('SET_GRAPHICS_STATE', { key: 'savedGraphics.total', value: total });
  },

  async getGraphicsTags({ commit }) {
    const { tags } = await Graphics.getTags();
    const sortedTags = [...tags].sort((a, b) => a.localeCompare(b));

    commit('SET_GRAPHICS_STATE', { key: 'templateTags', value: sortedTags });
  },

  changeGraphicTags(context, tags) {
    context.commit('SET_GRAPHICS_STATE', { key: 'tags', value: tags });

    // Clear current templates first, and reset page back to 1
    context.commit('SET_GRAPHICS_STATE', { key: 'templates', value: [] });
    context.commit('SET_GRAPHICS_STATE', { key: 'page', value: 1 });

    // Then refetch with the updated search fields
    context.dispatch('getTemplates');
  },

  changeGraphicsSearch({ commit, dispatch }, search) {
    commit('SET_GRAPHICS_STATE', { key: 'search', value: search });

    // Clear current templates first, and reset page back to 1
    commit('SET_GRAPHICS_STATE', { key: 'templates', value: [] });
    commit('SET_GRAPHICS_STATE', { key: 'page', value: 1 });

    // Then refetch with the updated search fields
    dispatch('getTemplates');
  },

  resetSearch(context) {
    context.commit('CLEAR_TEMPLATE_TAGS');
    context.commit('SET_GRAPHICS_STATE', { key: 'search', value: '' });

    // Clear current templates first, and reset page back to 1
    context.commit('SET_GRAPHICS_STATE', { key: 'templates', value: [] });
    context.commit('SET_GRAPHICS_STATE', { key: 'page', value: 1 });

    // Then refetch with the updated search fields
    context.dispatch('getTemplates');
  },

  incrementTemplatesPage(context) {
    // Increment the page number in stage
    context.commit('SET_GRAPHICS_STATE', { key: 'page', value: context.state.page + 1 });

    // Then fetch the next page
    context.dispatch('getTemplates');
  },

  /**
   * Loop through timeline, grab unique graphic template_ids, and fetch those templates
   * so they exist in the lookup table. Otherwise they won't render properly in the playlist.
   * Will not refetch any graphics already fetched.
   */
  async getTimelineTemplates(context) {
    const { timeline } = context.rootState.production.production;

    if (!timeline) { return; }

    const templateIds = getTemplateIds(timeline, state.templatesLookupTable, state.templatesNotFoundMap);

    if (templateIds?.length) {
      context.commit('SET_TEMPLATES_LOADING', { ids: templateIds, loading: true });

      try {
        const templates = await Graphics.getMultipleTemplates(templateIds);

        if (templates?.length) {
          context.commit('ADD_TEMPLATES_TO_LOOKUP_TABLE', templates);
        }

        // Any template requested but missing from the response is presumed deleted.
        const returnedIds = new Set((templates || []).map((template) => template._id));
        const notFoundIds = templateIds.filter((id) => !returnedIds.has(id));
        if (notFoundIds.length) {
          context.commit('SET_TEMPLATES_NOT_FOUND', { ids: notFoundIds, notFound: true });
        }
      } catch (err) {
        console.error('Error fetching timeline templates:', err);

        toast.makeToast({
          heading: 'Error',
          icon: 'error',
          loaderBg: 'red',
          text: `Failed to load graphics in playlist.`,
        });
      } finally {
        context.commit('SET_TEMPLATES_LOADING', { ids: templateIds, loading: false });
      }
    }

    // Graphics can be added to the timeline from sources that only carry
    // partial template data (e.g. global search), leaving items without the
    // meta fields (like `typeLookup`) needed to resolve social and shoppable
    // elements for live outputs. Backfill those from the full template now
    // that it's available in the lookup table.
    context.commit('SYNC_TEMPLATE_META_TO_TIMELINE_ITEMS', { rootState: context.rootState });
  },

  async getTemplates(context, attempts = 0) {
    const { page, tags, limit, search } = context.state;

    context.commit('SET_GRAPHICS_STATE', { key: 'isLoading', value: true });

    let templates;
    let total;

    try {
      ({ templates, total } = await Graphics.getTemplates({ page, tags, limit, search }));
    } catch (err) {
      // eslint-disable-next-line no-console
      if (attempts >= 3) {
        console.error('Error getting user graphic templates:', err);
        context.commit('SET_GRAPHICS_STATE', { key: 'isLoading', value: false });
        throw err;
      }

      await sleep(1e3);
      context.dispatch('getTemplates', attempts + 1);
      return;
    }

    context.commit('SET_GRAPHICS_STATE', { key: 'isLoading', value: false });
    context.commit('ADD_TEMPLATES_TO_LOOKUP_TABLE', templates);
    context.commit('SET_TEMPLATES', templates);
    context.commit('SET_TEMPLATE_TOTAL', total);
  },

  /**
   * Clear templates array and reset pagination
   */
  resetTemplates(context) {
    context.commit('RESET_TEMPLATES');
  },

  /**
   * Create new saved graphic, tied to the current production.
   * @param {Object} context
   * @param {Object} payload.graphic - Graphic data.
   * @param {String} payload.title - Name for the new graphic.
   * @returns {Promise<Object>} New saved graphic object.
   */
  async createGraphic(context, payload) {
    // Copy only the fields we need to save
    // This also produces a shallow copy of the graphic
    const graphic = pick(payload.graphic, [
      'fields',
      'owner',
      'template_id',
    ]);

    // Attach some extra metadata
    graphic.production_id = context.rootState.production.production._id;
    graphic.title = payload.title || payload.graphic.title;
    graphic.owner = context.rootState.production.production.owner;

    // Save, update state, and return graphic
    const result = await Graphics.createGraphic(graphic);
    context.commit('CREATED_GRAPHIC', assign(graphic, pick(result, ['_id', 'created_by', 'created_time', 'meta'])));
    return graphic;
  },

  async duplicateGraphic(context, graphic) {
    const clone = cloneDeep(graphic);
    clone.title += ' - COPY';

    const savedGraphic = await Graphics.createGraphic(
      omit(clone, [
        '_id',
        'created_by',
        'last_updated',
        'created_time',
        'meta',
        'modified_time',
      ]),
    );

    const duplicate = assign(clone, pick(savedGraphic, ['_id', 'created_by', 'created_time']));
    context.commit('DUPLICATE_GRAPHIC', duplicate);
    return duplicate;
  },

  async deleteGraphic(context, graphicId) {
    await Graphics.deleteGraphic(graphicId);
    context.commit('DELETE_GRAPHIC', graphicId);
  },

  setGraphics(context, payload) {
    context.commit('SET_GRAPHICS', payload);
  },
};

const mutations = {
  TOGGLE_ASSET_MODAL(state, opts) {
    state.assetModalOpenType = opts.type;
    state.assetModalOpen = opts.toggled;
  },

  SET_GRAPHICS(state, payload) {
    state.graphics = payload;

    const obj = payload.reduce((memo, graphic) => ({
      ...memo,
      [graphic._id]: graphic,
    }), {});

    state.lookupTable = { ...state.lookupTable, ...obj};
  },

  CLEAR_TEMPLATE_TAGS(state) {
    state.tags = [];
  },

  /**
   * Set state property by key/value pair
   */
  SET_GRAPHICS_STATE(state, payload) {
    const { key, value } = payload;
    state[key] = value;
  },

  SET_TEMPLATES(state, templates) {
    state.templates = uniqBy(state.templates.concat(templates), '_id');
  },

  ADD_TEMPLATES_TO_LOOKUP_TABLE(state, templates) {
    // Merge templates into template lookup table
    state.templatesLookupTable = {
      ...state.templatesLookupTable,
      ...(templates.reduce((memo, template) => ({
        ...memo,
        [template._id]: {
          ...(state.templatesLookupTable[template._id] || {}),
          ...template,
        },
      }), {})),
    };
  },

  /**
   * Set loading flags for specific templates by ID,
   * so the playlist can show a skeleton while they load.
   */
  SET_TEMPLATES_LOADING(state, { ids, loading }) {
    state.templatesLoadingMap = {
      ...state.templatesLoadingMap,
      ...ids.reduce((memo, id) => ({
        ...memo,
        [id]: loading,
      }), {}),
    };
  },

  SET_TEMPLATES_NOT_FOUND(state, { ids, notFound }) {
    state.templatesNotFoundMap = {
      ...state.templatesNotFoundMap,
      ...ids.reduce((memo, id) => ({
        ...memo,
        [id]: notFound,
      }), {}),
    };
  },

  /**
   * Backfill `meta` on graphic timeline items from the full template
   * in the lookup table. Items added from sources that only return partial
   * template data (e.g. global search) can be missing fields like
   * `typeLookup` or `elementCounts`, which producer and live rely on to
   * resolve social/shoppable elements. Existing item meta is preserved.
   */
  SYNC_TEMPLATE_META_TO_TIMELINE_ITEMS(state, { rootState }) {
    const { timeline } = rootState?.production?.production || {};

    if (!timeline?.length) { return; }

    collectTimelineValues(timeline, (item) => {
      if (item?.layout !== 'graphic' || !item.template_id) {
        return null;
      }

      const template = state.templatesLookupTable[item.template_id];
      if (!template?.meta || typeof template.meta !== 'object') {
        return null;
      }

      const current = item.meta || {};
      const needsBackfill = Object.keys(template.meta)
        .some((key) => current[key] === undefined);

      if (needsBackfill) {
        item.meta = {
          ...template.meta,
          ...current,
        };
      }

      return null;
    }, new Set());
  },

  SET_TEMPLATE_TOTAL(state, total) {
    state.total = total;
  },

  RESET_TEMPLATES(state) {
    state.templates = [];
    state.page = 1;
    state.total = 0;
  },

  CREATED_GRAPHIC(state, payload) {
    state.graphics.unshift(payload);
    state.lookupTable[payload._id] = payload;

    state.lookupTable = { ...state.lookupTable};
  },

  DUPLICATE_GRAPHIC(state, payload) {
    state.graphics.unshift(payload);
    state.lookupTable[payload._id] = payload;

    state.lookupTable = { ...state.lookupTable};
  },

  DELETE_GRAPHIC(state, graphicId) {
    state.graphics = state.graphics.filter((g) => g._id !== graphicId);
    delete state.lookupTable[graphicId];
    state.lookupTable = { ...state.lookupTable};
  },
};

const getters = {
  /**
   * Whether or not there's still more graphic templates to load
   * @returns {Boolean}
   */
  hasMoreTemplates(state) {
    return state.total > (state.page * state.limit);
  },
};

export default {
  state,
  actions,
  mutations,
  getters,
};
